import 'react-app-polyfill/ie11';
import 'react-app-polyfill/stable';
import 'core-js/stable';
import 'regenerator-runtime/runtime';
import 'raf/polyfill';
import 'url-polyfill';
import 'intl';
import React from 'react';
import { PersistGate } from 'redux-persist/integration/react';
import * as Sentry from '@sentry/react';
import { render } from 'react-dom';
import { Provider } from 'react-redux';
import { browserHistory, Router } from 'react-router';
import CookieStorage from 'redux-persist-cookie-storage';
import { addLocaleData, IntlProvider } from 'react-intl';

import { getRoutes } from './app/routes';
import { configureStore } from './config';
import { localStorageReducers } from './app/local-reducers';
import {
  recursiveParse,
  isLocalStorageSupported,
} from './app/shared/utils/utils';
import { trackPage, isTrackingDisabled } from './app/shared/utils/tracking';
import {
  DEFAULT_LOCALE,
  SENTRY_RELEASE,
  SENTRY_DSN,
  STAGING,
  SENTRY_IS_ENABLED,
} from './app/config';

import locale_en from 'react-intl/locale-data/en';
import locale_de from 'react-intl/locale-data/de';
import messages_en from './locale/en.json';
import messages_de from './locale/de.json';

const mergeLocalStateIntoPreloadedState = (preloadedState) => {
  /* we need to do this because some users have cookies disabled which causes the
    browser to disallow access to localstorage
  */
  if (!isLocalStorageSupported()) {
    return preloadedState;
  }
  if (!preloadedState) return;
  Object.keys(localStorageReducers).forEach((key) => {
    const preloadedItem = preloadedState[key];

    if (preloadedItem) {
      const localStorageItem = recursiveParse(
        window.localStorage.getItem(`persist:${key}`)
      );

      preloadedState[key] = Object.assign(preloadedItem, localStorageItem);
    }
  });
  return preloadedState;
};

const preloadedState = mergeLocalStateIntoPreloadedState(
  window._PRELOADED_STATE__
);
const { store, persistor } = configureStore({
  preloadedState,
  history: browserHistory,
  storage: new CookieStorage({
    expiration: {
      default: 364 * 4 * 86400,
    },
  }),
});
const locale = DEFAULT_LOCALE;

const run = () => {
  // make sure we load the page scrolled to the top
  window.scrollTo(0, 0);

  browserHistory.listen((change) => {
    if (isTrackingDisabled() || change.state?.doNotTrack) {
      return;
    }
    trackPage(change);
  });

  window.store = store;

  addLocaleData([...locale_en, ...locale_de]);

  const messages = {
    de: messages_de,
    en: messages_en,
  };

  render(
    <IntlProvider defaultLocale={locale} locale={locale} messages={messages}>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <Router
            onUpdate={() => window.scrollTo(0, 0)}
            history={browserHistory}
          >
            {getRoutes(store)}
          </Router>
        </PersistGate>
      </Provider>
    </IntlProvider>,
    document.querySelector('#app')
  );
};

// Setup setnry
if (SENTRY_IS_ENABLED) {
  Sentry.init({
    dsn: SENTRY_DSN,
    release: SENTRY_RELEASE,
    environment: STAGING ? 'staging' : 'production',
    ignoreErrors: ['AbortError'],
    normalizeDepth: 7, // allows for us to debug 7 levels into the redux state
  });
}

// run the app
run();
